import React from "react";
import { Link } from "react-router-dom";
import LegalLayout, { DataCard } from "../components/legal/LegalLayout";
import { company, retention } from "../legal/company";

const ContactEmail = () => <a href={`mailto:${company.email}`}>{company.email}</a>;

const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

const sections = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    content: (
      <>
        <p>
          This website is operated by <strong>{company.legalName || company.name}</strong>,
          a software development company based in {company.address} ("Code
          Solutions", "we", "us"). We are responsible for the personal data
          collected through this website.
        </p>
        <p>
          For any question about this policy or your personal data, contact us
          at <ContactEmail /> or by phone at{" "}
          <a href={company.phoneHref}>{company.phone}</a>.
        </p>
        {company.cndpReference && (
          <p>
            This processing has been declared to the Moroccan data protection
            authority (CNDP) under reference{" "}
            <strong>{company.cndpReference}</strong>.
          </p>
        )}
      </>
    ),
  },
  {
    id: "scope",
    title: "2. Scope of this policy",
    content: (
      <p>
        This policy explains what personal data we collect when you visit{" "}
        {company.website.replace("https://", "")}, use our contact form or chat
        with our AI assistant, why we collect it, who we share it with and the
        rights you have. It is written to comply with Moroccan{" "}
        <strong>Law No. 09-08</strong> on the protection of individuals with
        regard to the processing of personal data and, for visitors in the
        European Economic Area, the <strong>General Data Protection Regulation
        (GDPR)</strong>.
      </p>
    ),
  },
  {
    id: "data-we-collect",
    title: "3. Data we collect and why",
    content: (
      <>
        <p>
          We only collect the data needed for the purposes below. We do not
          sell your data, and we do not use it for advertising.
        </p>

        <DataCard>
          <h3>Contact form</h3>
          <dl>
            <dt>Data</dt>
            <dd>Your name, email address, subject and message.</dd>
            <dt>Purpose</dt>
            <dd>
              To answer your request, discuss your project and send you an
              automatic confirmation that we received your message.
            </dd>
            <dt>Legal basis</dt>
            <dd>
              Steps taken at your request before a possible contract, and our
              legitimate interest in responding to inquiries.
            </dd>
            <dt>Retention</dt>
            <dd>
              {capitalize(retention.contact)}, unless a contract follows, in
              which case we keep what the law requires us to keep.
            </dd>
          </dl>
        </DataCard>

        <DataCard>
          <h3>AI assistant (chat)</h3>
          <dl>
            <dt>Data</dt>
            <dd>
              The messages you type, the assistant's replies, the date and time,
              a technical session identifier and your IP address.
            </dd>
            <dt>Purpose</dt>
            <dd>
              To generate answers to your questions, follow up on project
              inquiries, prevent abuse and improve the quality of our answers.
            </dd>
            <dt>Legal basis</dt>
            <dd>
              Our legitimate interest in answering visitors' questions about
              our services. Using the chat is optional.
            </dd>
            <dt>Retention</dt>
            <dd>{capitalize(retention.chat)}, after which conversations are deleted.</dd>
          </dl>
        </DataCard>

        <DataCard>
          <h3>Technical data</h3>
          <dl>
            <dt>Data</dt>
            <dd>
              IP address, browser type and the pages requested, recorded
              automatically by our hosting provider and servers.
            </dd>
            <dt>Purpose</dt>
            <dd>
              To deliver the website, keep it secure and limit abusive traffic
              (rate limiting).
            </dd>
            <dt>Legal basis</dt>
            <dd>Our legitimate interest in operating a secure website.</dd>
            <dt>Retention</dt>
            <dd>Short-term server logs, kept according to our providers' standard log retention.</dd>
          </dl>
        </DataCard>

        <p>
          Please do not share sensitive information (such as health data,
          passwords or financial details) in the contact form or the chat.
        </p>
      </>
    ),
  },
  {
    id: "ai-assistant",
    title: "4. About the AI assistant",
    content: (
      <>
        <p>
          The chat on this website is an automated assistant. Your messages
          are sent to <strong>OpenAI</strong>, which generates the replies on
          our behalf. According to OpenAI's API data usage policies, data sent
          through its API is not used to train OpenAI's models by default.
        </p>
        <p>
          Answers are generated automatically and may be incomplete or
          inaccurate. They are not a binding offer or quote. For anything
          important, contact our team directly.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "5. Who we share your data with",
    content: (
      <>
        <p>
          Your data is accessible only to the Code Solutions team members who
          need it. We also rely on the following service providers, who
          process data on our behalf and only according to our instructions:
        </p>
        <ul>
          <li>
            <strong>Vercel Inc.</strong> (United States): website and server
            hosting.
          </li>
          <li>
            <strong>OpenAI, L.L.C.</strong> (United States): generation of AI
            assistant replies.
          </li>
          <li>
            <strong>MongoDB, Inc.</strong> (MongoDB Atlas): storage of chat
            conversations.
          </li>
          <li>
            <strong>Email providers</strong> (Namecheap, Inc. and Google LLC):
            delivery and storage of contact form emails.
          </li>
          <li>
            <strong>Google LLC</strong> (Google Fonts): delivery of the fonts
            used on this website, which involves your browser connecting to
            Google's servers.
          </li>
        </ul>
        <p>
          We may also disclose data when required by law or by a competent
          authority, or to protect our rights.
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    title: "6. International transfers",
    content: (
      <p>
        Some of the providers listed above are located outside Morocco,
        mainly in the United States. When your data is transferred abroad, we
        do so in accordance with Law No. 09-08, including the conditions set by
        the CNDP where they apply, and we rely on providers that offer
        appropriate contractual and security safeguards.
      </p>
    ),
  },
  {
    id: "security",
    title: "7. How we protect your data",
    content: (
      <p>
        The website is served over an encrypted HTTPS connection, access to
        your data is restricted to authorized people, and we use reputable
        providers with strong security practices. No method of transmission or
        storage is completely secure, but we take reasonable technical and
        organizational measures to protect your data against loss, misuse and
        unauthorized access.
      </p>
    ),
  },
  {
    id: "your-rights",
    title: "8. Your rights",
    content: (
      <>
        <p>Under Law No. 09-08, you have the right to:</p>
        <ul>
          <li>
            <strong>be informed</strong> about how your data is used (which is
            the purpose of this policy);
          </li>
          <li>
            <strong>access</strong> the personal data we hold about you;
          </li>
          <li>
            <strong>correct, update or delete</strong> data that is inaccurate,
            incomplete or no longer needed;
          </li>
          <li>
            <strong>object</strong> to the processing of your data for
            legitimate reasons, and to its use for marketing purposes at any
            time and free of charge.
          </li>
        </ul>
        <p>
          If you are in the European Economic Area, you also have the rights
          to restrict processing, to data portability and to lodge a complaint
          with your local data protection authority.
        </p>
        <p>
          To exercise any of these rights, email us at <ContactEmail />. We
          may ask you to confirm your identity before responding, and we
          reply as soon as possible. If you are not satisfied with our answer,
          you can contact the Moroccan data protection authority, the{" "}
          <strong>CNDP</strong> (Commission Nationale de contrôle de la
          protection des Données à caractère Personnel), at{" "}
          <a href="https://www.cndp.ma" target="_blank" rel="noopener noreferrer">
            www.cndp.ma
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "9. Children",
    content: (
      <p>
        This website is intended for businesses and professionals. It is not
        directed at children, and we do not knowingly collect data from
        anyone under 16. If you believe a child has sent us personal data,
        contact us and we will delete it.
      </p>
    ),
  },
  {
    id: "cookies",
    title: "10. Cookies",
    content: (
      <p>
        This website does not use cookies, analytics or other tracking
        technologies. See our <Link to="/cookie-policy">Cookie Policy</Link>{" "}
        for details.
      </p>
    ),
  },
  {
    id: "changes",
    title: "11. Changes to this policy",
    content: (
      <p>
        We may update this policy when our services or legal requirements
        change. The date at the top of this page shows when it was last
        updated. Significant changes will be highlighted on this website.
      </p>
    ),
  },
];

const PrivacyPolicy = () => (
  <LegalLayout
    title="Privacy Policy"
    description="How Code Solutions collects, uses and protects your personal data, and the rights you have under Moroccan Law 09-08 and the GDPR."
    intro="We respect your privacy. This policy explains, in plain language, what data we collect through this website, why, and how you stay in control of it."
    path="/privacy-policy"
    sections={sections}
  />
);

export default PrivacyPolicy;
